import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Monitor, 
  Wifi, 
  Activity, 
  Zap,
  Plus,
  RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';

const DashboardPage = () => {
  const { api, provider } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentDevices, setRecentDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, devicesRes] = await Promise.all([
        api.get('/stats'),
        api.get('/devices')
      ]);
      setStats(statsRes.data);
      setRecentDevices(devicesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const statsCards = [
    { 
      label: 'Total Devices', 
      value: stats?.total_devices || 0, 
      icon: Monitor, 
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10'
    },
    { 
      label: 'Active Devices', 
      value: stats?.active_devices || 0, 
      icon: Activity, 
      color: 'text-green-400',
      bgColor: 'bg-green-500/10'
    },
    { 
      label: 'Online Now', 
      value: stats?.online_devices || 0, 
      icon: Wifi, 
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10'
    },
    { 
      label: 'Connections Today', 
      value: stats?.total_connections_today || 0, 
      icon: Zap, 
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back, {provider?.company_name}</p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="refresh-stats-button"
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <Link
            to="/devices"
            data-testid="add-device-button"
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Device
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statsCards.map(({ label, value, icon: Icon, color, bgColor }) => (
          <div key={label} className="dashboard-card stats-card" data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm mb-1">{label}</p>
                <p className="text-3xl font-bold text-foreground">{value}</p>
              </div>
              <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Devices */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Recent Devices</h2>
          <Link to="/devices" className="text-primary text-sm hover:underline">
            View All
          </Link>
        </div>

        {recentDevices.length === 0 ? (
          <div className="empty-state py-8">
            <Monitor className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-muted-foreground">No devices yet</p>
            <Link to="/devices" className="text-primary text-sm hover:underline mt-2 inline-block">
              Add your first device
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>MAC Address</th>
                  <th>Access Code</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentDevices.map((device) => (
                  <tr key={device.id} data-testid={`device-row-${device.id}`}>
                    <td className="font-medium">{device.name}</td>
                    <td className="mac-address text-green-400">{device.mac_address}</td>
                    <td>
                      <span className="font-mono text-lg font-bold text-primary tracking-wider">
                        {device.access_code}
                      </span>
                    </td>
                    <td>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        device.status === 'active' ? 'status-active' : 'status-inactive'
                      }`}>
                        {device.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
